import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  ButtonGroup
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Receipt,
  RefreshCw,
  Filter,
  ShieldAlert,
  AlertCircle,
  Smartphone,
  FileSpreadsheet
} from "lucide-react";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getWhatsAppConsumption } from "../../services/whatsappConsumptionService";
import toastError from "../../errors/toastError";
import KpiCards from "./KpiCards";
import ChartsSection from "./ChartsSection";
import DailyTable from "./DailyTable";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    minHeight: "100vh"
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    gap: theme.spacing(2)
  },
  titleWrapper: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5)
  },
  title: {
    fontWeight: 700,
    fontSize: "1.5rem",
    color: theme.palette.text.primary
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem"
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap"
  },
  exportButton: {
    backgroundColor: "#16A34A",
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 2px 6px rgba(22, 163, 74, 0.25)",
    "&:hover": {
      backgroundColor: "#15803D"
    }
  },
  filterPaper: {
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
  },
  filterGrid: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2)
  },
  presetGroup: {
    marginBottom: theme.spacing(2)
  },
  loadingBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8),
    gap: theme.spacing(2)
  },
  unauthorizedPaper: {
    padding: theme.spacing(5),
    textAlign: "center",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    marginTop: theme.spacing(4)
  },
  emptyPaper: {
    padding: theme.spacing(4),
    textAlign: "center",
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    marginTop: theme.spacing(2)
  }
}));

const WhatsAppConsumption = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState(null);
  const [whatsappId, setWhatsappId] = useState("all");

  const todayStr = format(new Date(), "yyyy-MM-dd");
  const thirtyDaysAgoStr = format(subDays(new Date(), 30), "yyyy-MM-dd");

  const [startDate, setStartDate] = useState(thirtyDaysAgoStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [availableConnections, setAvailableConnections] = useState([]);

  const fetchConsumption = useCallback(
    async (forceRefresh = false) => {
      if (user?.profile !== "admin") return;

      try {
        setLoading(true);
        const res = await getWhatsAppConsumption({
          whatsappId: whatsappId === "all" ? undefined : whatsappId,
          startDate,
          endDate,
          refresh: forceRefresh ? "true" : undefined
        });
        setData(res);
        if (res?.connections?.length) {
          setAvailableConnections((prev) => {
            const map = new Map();
            prev.forEach((c) => map.set(c.id, c));
            res.connections.forEach((c) => map.set(c.id, c));
            return Array.from(map.values());
          });
        }
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    },
    [user, whatsappId, startDate, endDate]
  );

  useEffect(() => {
    fetchConsumption();
  }, [fetchConsumption]);

  // Exportar Excel Completo respeitando rigorosamente os filtros selecionados
  const handleExportExcel = async () => {
    if (user?.profile !== "admin") return;

    try {
      setExporting(true);

      // Busca dados com os parâmetros atualmente selecionados no filtro para garantir precisão
      const currentParams = {
        whatsappId: whatsappId === "all" ? undefined : whatsappId,
        startDate,
        endDate
      };

      const res = await getWhatsAppConsumption(currentParams);
      if (res) {
        setData(res);
      }

      const activeData = res || data;
      if (!activeData) {
        toast.warn("Não há dados disponíveis para exportação no período selecionado.");
        return;
      }

      const currency = activeData.currency || "USD";

      // Formatar data YYYY-MM-DD para DD/MM/AAAA
      const formatDatePtBr = (dateStr) => {
        if (!dateStr) return "-";
        const parts = dateStr.split("-");
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateStr;
      };

      const startPt = formatDatePtBr(startDate);
      const endPt = formatDatePtBr(endDate);

      // Obter nome legível da conexão selecionada
      let connectionLabel = "Todas as Conexões Oficiais";
      if (whatsappId !== "all") {
        const connList = availableConnections.length > 0 ? availableConnections : (activeData.connections || []);
        const found = connList.find((c) => String(c.id) === String(whatsappId));
        if (found) {
          connectionLabel = `${found.name}${found.number ? ` (${found.number})` : ""}`;
        }
      }

      const wb = XLSX.utils.book_new();

      // ==========================================
      // ABA 1: Resumo e Categorias
      // ==========================================
      const summaryRows = [
        ["RELATÓRIO DE CONSUMO WHATSAPP BUSINESS - META CLOUD API"],
        [`Período Filtrado: ${startPt} até ${endPt}`],
        [`Conexão Selecionada: ${connectionLabel}`],
        [`Moeda da Conta: ${currency}`],
        [`Data de Emissão: ${format(new Date(), "dd/MM/yyyy HH:mm:ss")}`],
        [],
        ["INDICADORES GERAIS"],
        ["Métrica", "Valor"],
        ["Total Gasto", Number(Number(activeData.totalCost || 0).toFixed(2))],
        ["Mensagens Enviadas (Faturadas)", Number(activeData.totalVolume || 0)],
        ["Custo Médio / Mensagem", Number(Number(activeData.averageCostPerMessage || 0).toFixed(4))],
        [],
        ["GASTOS POR CATEGORIA DE CONVERSA"],
        [
          "Categoria",
          "Mensagens (Volume)",
          "Participação no Volume (%)",
          `Total Gasto (${currency})`,
          `Custo Médio / Msg (${currency})`,
          "Participação no Custo (%)"
        ]
      ];

      const categoriesConfig = [
        { key: "MARKETING", label: "Marketing" },
        { key: "SERVICE", label: "Serviço" },
        { key: "UTILITY", label: "Utilidade" },
        { key: "AUTHENTICATION", label: "Autenticação" }
      ];

      const totalVol = Number(activeData.totalVolume || 0);
      const totalCst = Number(activeData.totalCost || 0);

      categoriesConfig.forEach((cat) => {
        const catData = activeData.byCategory?.[cat.key] || { volume: 0, cost: 0 };
        const vol = Number(catData.volume || 0);
        const cost = Number(Number(catData.cost || 0).toFixed(2));
        const volPercent = totalVol > 0 ? Number(((vol / totalVol) * 100).toFixed(1)) : 0;
        const costPercent = totalCst > 0 ? Number(((cost / totalCst) * 100).toFixed(1)) : 0;
        const avgCost = vol > 0 ? Number((cost / vol).toFixed(4)) : 0;

        summaryRows.push([
          cat.label,
          vol,
          `${volPercent}%`,
          cost,
          avgCost,
          `${costPercent}%`
        ]);
      });

      // Linha de total das categorias
      summaryRows.push([]);
      summaryRows.push([
        "TOTAL GERAL",
        totalVol,
        "100.0%",
        Number(totalCst.toFixed(2)),
        totalVol > 0 ? Number((totalCst / totalVol).toFixed(4)) : 0,
        "100.0%"
      ]);

      const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
      wsSummary["!cols"] = [
        { wch: 35 },
        { wch: 26 },
        { wch: 26 },
        { wch: 22 },
        { wch: 25 },
        { wch: 25 }
      ];
      XLSX.utils.book_append_sheet(wb, wsSummary, "Resumo e Categorias");

      // ==========================================
      // ABA 2: Detalhamento Diário
      // ==========================================
      const dailyRows = [
        ["DETALHAMENTO DIÁRIO DE CONSUMO E CUSTOS"],
        [`Período Filtrado: ${startPt} até ${endPt} | Conexão: ${connectionLabel}`],
        [`Moeda: ${currency}`],
        [],
        [
          "Data",
          "Total Mensagens",
          "Marketing (Msgs)",
          "Serviço (Msgs)",
          "Utilidade (Msgs)",
          "Autenticação (Msgs)",
          `Marketing (${currency})`,
          `Serviço (${currency})`,
          `Utilidade (${currency})`,
          `Autenticação (${currency})`,
          `Gasto do Dia (${currency})`
        ]
      ];

      const dailyHistory = activeData.dailyHistory || [];

      dailyHistory.forEach((day) => {
        const mktVol = Number(day.categories?.MARKETING?.volume || 0);
        const srvVol = Number(day.categories?.SERVICE?.volume || 0);
        const utlVol = Number(day.categories?.UTILITY?.volume || 0);
        const authVol = Number(day.categories?.AUTHENTICATION?.volume || 0);

        const mktCost = Number(Number(day.categories?.MARKETING?.cost || 0).toFixed(2));
        const srvCost = Number(Number(day.categories?.SERVICE?.cost || 0).toFixed(2));
        const utlCost = Number(Number(day.categories?.UTILITY?.cost || 0).toFixed(2));
        const authCost = Number(Number(day.categories?.AUTHENTICATION?.cost || 0).toFixed(2));
        const dayTotalCost = Number(Number(day.totalCost || 0).toFixed(2));

        dailyRows.push([
          formatDatePtBr(day.date),
          Number(day.totalVolume || 0),
          mktVol,
          srvVol,
          utlVol,
          authVol,
          mktCost,
          srvCost,
          utlCost,
          authCost,
          dayTotalCost
        ]);
      });

      // Linha de totalizador do período
      dailyRows.push([]);
      dailyRows.push([
        "TOTAL DO PERÍODO",
        totalVol,
        categoriesConfig.reduce((acc, c) => acc + Number(activeData.byCategory?.[c.key]?.volume || 0), 0),
        Number(activeData.byCategory?.SERVICE?.volume || 0),
        Number(activeData.byCategory?.UTILITY?.volume || 0),
        Number(activeData.byCategory?.AUTHENTICATION?.volume || 0),
        Number(Number(activeData.byCategory?.MARKETING?.cost || 0).toFixed(2)),
        Number(Number(activeData.byCategory?.SERVICE?.cost || 0).toFixed(2)),
        Number(Number(activeData.byCategory?.UTILITY?.cost || 0).toFixed(2)),
        Number(Number(activeData.byCategory?.AUTHENTICATION?.cost || 0).toFixed(2)),
        Number(totalCst.toFixed(2))
      ]);

      const wsDaily = XLSX.utils.aoa_to_sheet(dailyRows);
      wsDaily["!cols"] = [
        { wch: 14 },
        { wch: 16 },
        { wch: 16 },
        { wch: 16 },
        { wch: 16 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 20 }
      ];
      XLSX.utils.book_append_sheet(wb, wsDaily, "Detalhamento Diário");

      // Gerar e disparar download do arquivo Excel
      const cleanConn = connectionLabel.replace(/[^a-zA-Z0-9]/g, "_").slice(0, 20);
      const fileName = `consumo_whatsapp_${startPt.replace(/\//g, "-")}_a_${endPt.replace(/\//g, "-")}_${cleanConn}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success("Relatório de consumo exportado em Excel com sucesso!");
    } catch (err) {
      toastError(err);
    } finally {
      setExporting(false);
    }
  };

  // Seletor de atalhos rápidos de período
  const applyPreset = (preset) => {
    const now = new Date();
    if (preset === "7days") {
      setStartDate(format(subDays(now, 7), "yyyy-MM-dd"));
      setEndDate(format(now, "yyyy-MM-dd"));
    } else if (preset === "30days") {
      setStartDate(format(subDays(now, 30), "yyyy-MM-dd"));
      setEndDate(format(now, "yyyy-MM-dd"));
    } else if (preset === "month") {
      setStartDate(format(startOfMonth(now), "yyyy-MM-dd"));
      setEndDate(format(endOfMonth(now), "yyyy-MM-dd"));
    }
  };

  // Se o usuário não for admin, exibe tela de acesso restrito
  if (user?.profile !== "admin") {
    return (
      <Box className={classes.root}>
        <Paper className={classes.unauthorizedPaper} elevation={0}>
          <Box display="flex" justifyContent="center" mb={2}>
            <ShieldAlert size={48} color="#ef4444" />
          </Box>
          <Typography variant="h5" gutterBottom style={{ fontWeight: 700 }}>
            Acesso Restrito
          </Typography>
          <Typography color="textSecondary">
            Esta página é exclusiva para administradores do sistema.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const connections = data?.connections || [];
  const officialConnections = connections.filter((c) => c.waba_id);

  return (
    <Box className={classes.root}>
      {/* Cabeçalho */}
      <Box className={classes.header}>
        <Box className={classes.titleWrapper}>
          <Receipt size={32} color="#2563eb" />
          <Box>
            <Typography className={classes.title}>
              Consumo WhatsApp Business
            </Typography>
            <Typography className={classes.subtitle}>
              Monitoramento oficial de mensagens e custos da Meta Cloud API
            </Typography>
          </Box>
        </Box>

        <Box className={classes.headerActions}>
          <Button
            variant="contained"
            className={classes.exportButton}
            startIcon={
              exporting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <FileSpreadsheet size={18} />
              )
            }
            onClick={handleExportExcel}
            disabled={exporting || loading || connections.length === 0}
          >
            {exporting ? "Gerando Excel..." : "Exportar Excel (.xlsx)"}
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshCw size={18} />}
            onClick={() => fetchConsumption(true)}
            disabled={loading}
          >
            Atualizar Dados
          </Button>
        </Box>
      </Box>

      {/* Painel de Filtros */}
      <Paper className={classes.filterPaper} elevation={0}>
        <Box className={classes.presetGroup}>
          <ButtonGroup size="small" variant="outlined" color="primary">
            <Button onClick={() => applyPreset("7days")}>Últimos 7 dias</Button>
            <Button onClick={() => applyPreset("30days")}>Últimos 30 dias</Button>
            <Button onClick={() => applyPreset("month")}>Mês Atual</Button>
          </ButtonGroup>
        </Box>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="Data Inicial"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="Data Final"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} sm={4} md={4}>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel id="conn-select-label">Conexão WhatsApp</InputLabel>
              <Select
                labelId="conn-select-label"
                value={whatsappId}
                onChange={(e) => setWhatsappId(e.target.value)}
                label="Conexão WhatsApp"
              >
                <MenuItem value="all">Todas as Conexões Oficiais</MenuItem>
                {(availableConnections.length > 0 ? availableConnections : connections).map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name} {c.number ? `(${c.number})` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12} md={2}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<Filter size={18} />}
              onClick={() => fetchConsumption(false)}
              disabled={loading}
            >
              Filtrar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Conteúdo */}
      {loading ? (
        <Box className={classes.loadingBox}>
          <CircularProgress size={42} />
          <Typography color="textSecondary">
            Consultando métricas na Meta Graph API...
          </Typography>
        </Box>
      ) : connections.length === 0 ? (
        <Paper className={classes.emptyPaper} elevation={0}>
          <Box display="flex" justifyContent="center" mb={1.5}>
            <Smartphone size={40} color="#9ca3af" />
          </Box>
          <Typography variant="h6" gutterBottom style={{ fontWeight: 600 }}>
            Nenhuma Conexão Oficial Configurada
          </Typography>
          <Typography color="textSecondary">
            Esta empresa ainda não possui números conectados via WhatsApp Oficial (Meta Cloud API).
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {/* Cards de Métricas */}
          <Grid item xs={12}>
            <KpiCards summary={data} />
          </Grid>

          {/* Gráficos */}
          <Grid item xs={12}>
            <ChartsSection
              dailyHistory={data?.dailyHistory || []}
              byCategory={data?.byCategory || {}}
              currency={data?.currency || "USD"}
            />
          </Grid>

          {/* Tabela Analítica Diária */}
          <Grid item xs={12}>
            <DailyTable
              dailyHistory={data?.dailyHistory || []}
              currency={data?.currency || "USD"}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default WhatsAppConsumption;
